import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { LayoutDashboard, Loader2 } from 'lucide-react';
import useAuthStore from '../store/authStore';

export default function Login() {
  const [identifier, setIdentifier] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [brand, setBrand] = useState(null);
  const [orgChoices, setOrgChoices] = useState(null);
  const [choosingOrgId, setChoosingOrgId] = useState(null);
  const login = useAuthStore((s) => s.login);
  const navigate = useNavigate();

  useEffect(() => {
    fetch('/api/v1/public/brand')
      .then((r) => r.json())
      .then((d) => { if (d.success) setBrand(d.data); })
      .catch(() => {});
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const result = await login(identifier, password);
      if (result?.needsOrgChoice) {
        setOrgChoices(result.orgs);
        return;
      }
      navigate('/');
    } catch (err) {
      setError(err.message || 'Erro ao fazer login');
    } finally {
      setLoading(false);
    }
  };

  const handleChooseOrg = async (org) => {
    setError('');
    setChoosingOrgId(org.id);
    try {
      await login(identifier, password, org.id);
      navigate('/');
    } catch (err) {
      setError(err.message || 'Erro ao fazer login');
    } finally {
      setChoosingOrgId(null);
    }
  };

  const handleBackToLogin = () => {
    setOrgChoices(null);
    setError('');
  };

  const year = new Date().getFullYear();
  const brandName = brand?.header_name || 'Arco';

  return (
    <div className="min-h-screen relative overflow-hidden flex items-center justify-center px-4 bg-[#0f0f10]">
      {/* Glow suave (esquerda-cima) */}
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          background:
            'radial-gradient(circle at 20% 30%, rgba(255,255,255,0.10) 0%, rgba(255,255,255,0.04) 22%, rgba(0,0,0,0) 55%)',
        }}
      />
      {/* Glow suave (direita-baixo) */}
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          background:
            'radial-gradient(circle at 82% 72%, rgba(255,255,255,0.08) 0%, rgba(255,255,255,0.03) 25%, rgba(0,0,0,0) 55%)',
        }}
      />

      {/* Card de login */}
      <form
        onSubmit={handleSubmit}
        className="relative z-10 w-full max-w-[340px] flex flex-col items-center"
      >
        <div className="flex items-center justify-center mb-8">
          {brand?.login_logo_url ? (
            <img
              src={brand.login_logo_url}
              alt={brandName}
              className="object-contain"
              style={{ height: `${Math.min(brand?.login_logo_size || 120, 300)}px`, width: 'auto' }}
            />
          ) : brand?.logo_url ? (
            <img
              src={brand.logo_url}
              alt={brandName}
              className="object-contain"
              style={{ height: `${Math.min((brand?.logo_size || 40) * 3, 150)}px`, width: 'auto' }}
            />
          ) : (
            <LayoutDashboard className="w-20 h-20 text-accent" />
          )}
        </div>

        {error && (
          <div className="w-full mb-3 px-3 py-2 rounded-md bg-red-500/10 border border-red-500/30 text-red-300 text-xs text-center">
            {error}
          </div>
        )}

        {orgChoices ? (
          <div className="w-full flex flex-col items-center">
            <p className="w-full mb-3 text-sm text-white/80 text-center">
              Em qual organização você quer entrar?
            </p>
            <div className="w-full flex flex-col gap-2 mb-1">
              {orgChoices.map((org) => (
                <button
                  key={org.id}
                  type="button"
                  onClick={() => handleChooseOrg(org)}
                  disabled={choosingOrgId != null}
                  className="w-full bg-white/[0.04] border border-white/10 rounded-md py-2.5 px-3.5 text-left text-sm text-white hover:bg-white/[0.08] focus:outline-none focus:border-white/30 transition-colors disabled:opacity-60 flex items-center justify-between gap-2"
                >
                  <span className="min-w-0">
                    <span className="block truncate">{org.name}</span>
                    <span className="block text-[11px] text-white/40 truncate">{org.slug}</span>
                  </span>
                  {choosingOrgId === org.id && <Loader2 className="w-4 h-4 shrink-0 animate-spin" />}
                </button>
              ))}
            </div>
            <button
              type="button"
              className="mt-3 text-[11px] text-accent/80 hover:text-accent transition-colors"
              onClick={handleBackToLogin}
            >
              Voltar
            </button>
          </div>
        ) : (
          <>
            <input
              type="text"
              value={identifier}
              onChange={(e) => setIdentifier(e.target.value)}
              placeholder="Email"
              autoComplete="username"
              required
              className="w-full bg-white/[0.04] border border-white/10 rounded-md py-2.5 px-3.5 text-sm text-white placeholder:text-white/40 focus:outline-none focus:border-white/30 transition-colors mb-2.5"
            />

            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Password"
              autoComplete="current-password"
              required
              className="w-full bg-white/[0.04] border border-white/10 rounded-md py-2.5 px-3.5 text-sm text-white placeholder:text-white/40 focus:outline-none focus:border-white/30 transition-colors mb-4"
            />

            <button
              type="submit"
              disabled={loading}
              className="w-full bg-white/[0.06] hover:bg-white/[0.10] border border-white/10 text-white/90 text-sm font-medium py-2.5 rounded-md flex items-center justify-center gap-2 transition-colors disabled:opacity-60"
            >
              {loading && <Loader2 className="w-4 h-4 animate-spin" />}
              {loading ? 'Entrando...' : 'Login'}
            </button>

            <button
              type="button"
              className="mt-3 text-[11px] text-accent/80 hover:text-accent transition-colors"
              onClick={(e) => { e.preventDefault(); setError('Contate o administrador para redefinir a senha.'); }}
            >
              Forgot password?
            </button>
          </>
        )}
      </form>

      {/* Footer */}
      <div className="absolute bottom-5 left-0 right-0 text-center text-[11px] text-white/30">
        © {year} {brandName}. Todos os direitos reservados.
      </div>
    </div>
  );
}
